import {cn} from '@sollapay/ui';
import {BreadcrumbsRow, HeaderMetaRow, Skeleton, Typography} from '@sollapay/ui/components';
import {useGoBack, useGoForward} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {
  TrustAccountBadge,
  TrustAccountBreadcrumbs,
  TrustAccountNumber
} from '@/features/trust-accounts/components';
import {TRUST_PURPOSE_CONFIG} from '@/features/trust-accounts/config';

import {ACTIVATION_CONTENT_WIDTH} from '../constants';

import type {TrustAccountSkeletonDraftDTO} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

export interface ActivationPageHeaderSectionProps {
  trustAccount: TrustAccountSkeletonDraftDTO | undefined;
  action?: ReactNode;
}

export const ActivationPageHeaderSection: FC<ActivationPageHeaderSectionProps> = ({
  trustAccount,
  action
}) => {
  const {t} = useTranslation('trustAccounts');
  const back = useGoBack();
  const forward = useGoForward();

  return (
    <section
      className="flex flex-col items-center pt-12"
      data-slot="trust-account-activation-header"
    >
      <div
        className={cn('flex w-full flex-col items-start gap-3', ACTIVATION_CONTENT_WIDTH)}
        data-slot="activation-page-header-section"
      >
        <div className="flex w-full items-center justify-between gap-6">
          <BreadcrumbsRow back={back} forward={forward}>
            <TrustAccountBreadcrumbs
              title={t('activation.title')}
              name={trustAccount?.name}
              trustAccountId={trustAccount?.id}
            />
          </BreadcrumbsRow>
          {action && (
            <div className="shrink-0" data-slot="activation-header-action">
              {action}
            </div>
          )}
        </div>

        <div className="flex w-full flex-col gap-2">
          <div className="flex">
            {trustAccount ? (
              <Typography as="h1" size="display-sm" weight="semibold" className="max-w-2xl">
                {trustAccount.name}
              </Typography>
            ) : (
              <Skeleton className="h-9 w-56" />
            )}
          </div>

          {/* Account number, account type and status row */}
          <div className="flex w-full items-center justify-between gap-4">
            {trustAccount ? (
              <HeaderMetaRow
                wrap
                items={[
                  {
                    custom: <TrustAccountNumber refId={trustAccount.refId} variant="labeled" />,
                    key: 'account-number'
                  },
                  {
                    icon: 'home',
                    tooltip: t('activation.accountType'),
                    value:
                      trustAccount.trustPurpose &&
                      t(TRUST_PURPOSE_CONFIG[trustAccount.trustPurpose].labelKey)
                  }
                ]}
              />
            ) : (
              <Skeleton className="h-6 w-64 rounded-md" />
            )}

            {trustAccount?.trustStatus ? (
              <TrustAccountBadge status={trustAccount.trustStatus} />
            ) : (
              <Skeleton className="h-5 w-24 rounded-full" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
